import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@components/atoms/Button';
import { AlertTriangle } from 'lucide-react';

// ============================================
// Error Boundary Component
// ============================================

/**
 * ErrorBoundary - Vangt errors in child componenten en toont een fallback UI.
 * 
 * Gebruik:
 * ```tsx
 * <ErrorBoundary fallback={<CustomErrorComponent />}>
 *   <RiskyComponent />
 * </ErrorBoundary>
 * ```
 * 
 * Regels (zie 01_FRONTEND_GUARDRAILS.md):
 * - Gebruik voor componenten die kunnen crashen
 * - Toon altijd een user-friendly error message
 * - Log errors in development
 */

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  // Update state wanneer een error optreedt in een child component
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  // Log de error en error info
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught error:', error, errorInfo);
    
    // Roep custom onError handler aan als deze is meegegeven
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  // Reset de error state (voor retry)
  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    // Als er een error is, toon de fallback UI
    if (hasError) {
      // Gebruik custom fallback als deze is meegegeven
      if (fallback) {
        return fallback;
      }

      // Standaard fallback UI
      return (
        <div
          className="min-h-screen flex flex-col items-center justify-center p-4"
          role="alert"
        >
          <div className="text-center max-w-md">
            <AlertTriangle className="w-16 h-16 text-error-500 mx-auto mb-4" />
            
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Er is een fout opgetreden
            </h2>
            
            <p className="text-gray-600 mb-6">
              Er is iets misgegaan tijdens het laden van deze pagina.
              Probeer het opnieuw of neem contact op met de beheerder.
            </p>
            
            {error && (
              <details className="mb-6 text-left">
                <summary className="cursor-pointer text-sm font-medium text-gray-500 hover:text-gray-700">
                  Technische details
                </summary>
                <div className="mt-2 p-3 bg-gray-50 rounded-lg text-xs text-gray-600 font-mono">
                  <p><strong>Error:</strong> {error.message}</p>
                  <p><strong>Stack:</strong></p>
                  <pre className="overflow-x-auto">{error.stack}</pre>
                </div>
              </details>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="primary" onClick={this.resetError}>
                Probeer opnieuw
              </Button>
              <Button
                variant="secondary"
                onClick={() => window.location.reload()}
              >
                Vernieuw pagina
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Geen error, render children
    return children;
  }
}

export default ErrorBoundary;
